import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useSpring, animated } from 'react-spring'
import { withTranslationCustom } from 'lib/i18next'

import { CopyToClipboard } from 'react-copy-to-clipboard'
import Button from 'components/UI/Button'

import './copy-clipboard-button.pcss'
import Icon from 'components/Icon'

/**
 * CopiedOverlay component to display a message when text is copied.
 * @param {Object} props - Component props.
 * @param {string} props.copiedText - The text to display when copied.
 * @returns {JSX.Element} The CopiedOverlay component.
 */
const CopiedOverlay = ({ copiedText }) => {
    const enterSpring = useSpring({
        config: {
            duration: 500,
        },
        delay: 2000,
        from: {
            opacity: 1,
        },
        to: {
            opacity: 0,
        }
    })

    return (
        <animated.div className="copy-clipboard-button__copy-overlay" style={enterSpring}>
            {copiedText}
        </animated.div>
    )
}

/**
 * CopyClipboardButton component to copy text to clipboard and display a message.
 * @param {Object} props - Component props.
 * @param {string} props.value - The text to copy to clipboard.
 * @param {string} [props.className] - Additional class names for the button.
 * @param {string} [props.copiedText='common:linkCopied'] - The text to display when copied.
 * @returns {JSX.Element} The CopyClipboardButton component.
 */
const CopyClipboardButton = ({ t, className, value, copiedText = 'common:linkCopied' }) => {
    const [copyCount, setCopyCount] = useState(0)

    return (
        <>
            <CopyToClipboard
                text={value}
                onCopy={() => {
                    setCopyCount(copyCount + 1)
                }}
            >
                <Button
                    type="button"
                    className="button button--plain dev-error__copyButton"
                    onClick={() => null}
                    trackName="cancel"
                >
                    <Icon name="icon_copy" />
                </Button>
            </CopyToClipboard>
            {copyCount > 0 && <CopiedOverlay key={copyCount} copiedText={t(copiedText)} />}
        </>
    )
}

CopyClipboardButton.propTypes = {
    value: PropTypes.string.isRequired,
    className: PropTypes.string,
    copiedText: PropTypes.string,
}

export default withTranslationCustom()(CopyClipboardButton)
